import React, { useCallback, useEffect, useState } from 'react';
import toUpper from 'lodash/toUpper';
import snakeCase from 'lodash/snakeCase';
import get from 'lodash/get';
import capitalize from 'lodash/capitalize';
import { Redirect, Switch, Route, Link } from 'react-router-dom';
import Auth from '@aws-amplify/auth';
import { QRCodeSVG } from 'qrcode.react';
import styled from 'styled-components';
import { Menu, Icon, Layout, Button, Select } from 'antd';
import { SegmentGroup } from '@fxtr/i18n';
import config from '$config';
import { isMotrio, isRenault, getSiderChrome } from '$app/brand';
import history from '$app/utils/history';
import { getMenuItemsForGroups } from '$app/menuPermissions';
import { useMaiaT } from '$app/i18n/useMaiaT';
import LanguageSelector from '$app/components/LanguageSelector';
import {
  getSelectableSegmentsFromGroups,
  getSelectedSegmentFromGroups,
  setStoredSelectedSegment,
} from '$app/segmentSelection';

import Dashboard from '$app/apps/dashboard/src/app';
import Allocations from '$app/apps/allocations';
import Bookings from '$app/apps/bookings';
import Capacity from '$app/apps/capacity/src/app';
import Garages from '$app/apps/garages/src/app';
import Productlist from '$app/apps/productlist/src/app';
import Heroes from '$app/apps/heroes/src/app';
import CoverageZones from '$app/apps/coverage-zones/src/app';
import Campaigns from '$app/apps/campaigns/src/app';

const { Sider, Content } = Layout;

const DASHBOARD = 'DASHBOARD';
const ALLOCATIONS = 'ALLOCATIONS';
const BOOKINGS = 'BOOKINGS';
const CAPACITY = 'CAPACITY';
const GARAGES = 'GARAGES';
const PRODUCT = 'PRODUCT';
const HEROES = 'HEROES';
const COVERAGE_ZONES = 'COVERAGE_ZONES';
const CAMPAIGNS = 'CAMPAIGNS';

const sidebarStateMap = {
  '/dashboard': ['1'],
  '/dashboard/allocations': ['2'],
  '/allocations': ['2'],
  '/bookings': ['3'],
  '/capacity': ['4'],
  '/garages': ['5'],
  '/coverage-zones': ['6'],
  '/heroes': ['7'],
  '/product': ['8'],
  '/campaigns': ['10'],
};

const transitionStyle = {
  transition: 'all 0.2s',
  transitionProperty: 'all',
  transitionDuration: '0.2s',
  transitionTimingFunction: 'ease',
  transitionDelay: '0s',
};

// FIXTER operates within a single segment group, so the segment/language controls are
// hidden. Motrio keeps both; Renault keeps language only (single-country deployment).
const showSegmentLanguageBar = config.segmentGroup !== SegmentGroup.FIXTER;
const showCountrySwitcher = showSegmentLanguageBar && !isRenault;

// Maps a segment's trailing country code (e.g. MOTRIO-UK -> UK) to a country label.
const COUNTRY_LABELS = {
  UK: ['maia.country.uk', 'United Kingdom'],
  FR: ['maia.country.fr', 'France'],
  IT: ['maia.country.it', 'Italy'],
  ES: ['maia.country.es', 'Spain'],
  PT: ['maia.country.pt', 'Portugal'],
  NL: ['maia.country.nl', 'Netherlands'],
  MA: ['maia.country.ma', 'Morocco'],
};

const getSegmentCountryLabel = (segment, t) => {
  const countryCode = segment.split('-')[1];
  const entry = COUNTRY_LABELS[countryCode];
  return entry ? t(entry[0], entry[1]) : segment;
};

const {
  background: siderBackground,
  foreground: siderForeground,
  selectedBackground: siderSelectedBackground,
  selectedForeground: siderSelectedForeground,
} = getSiderChrome();

const SiderStyled = siderBackground
  ? styled(Sider)`
      &.ant-layout-sider,
      .ant-menu,
      .ant-layout-sider-trigger {
        background-color: ${siderBackground} !important;
      }

      .ant-menu-item:hover {
        text-decoration-line: underline !important;
      }

      .ant-btn-link {
        color: ${siderForeground} !important;
      }

      .ant-menu-item,
      .ant-menu-item > a,
      .ant-menu-item i,
      .ant-menu-item span,
      .ant-layout-sider-trigger {
        color: ${siderForeground} !important;
      }

      .ant-menu-item-selected {
        background-color: ${siderSelectedBackground} !important;
        i,
        span,
        a {
          color: ${siderSelectedForeground} !important;
        }
      }
    `
  : Sider;

const BrandLabel = styled.div`
  color: ${siderForeground};
  font-weight: 700;
  letter-spacing: 1px;
  margin: 10px auto;
  text-align: center;
`;

function MFASetup() {
  const t = useMaiaT();
  const [showSetup, setShowSetup] = useState(false);
  const [qrCodeURL, setQrCodeURL] = useState(null);
  const [secret, setSecret] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);

  const checkMFAStatus = useCallback(async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const preferredMFA = await Auth.getPreferredMFA(user);
      return preferredMFA !== 'NOMFA';
    } catch (err) {
      console.error('Error checking MFA status:', err);
      return false;
    }
  }, []);

  useEffect(() => {
    const run = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const username = user.username;
        const mfaDismissed = localStorage.getItem(`mfa-setup-dismissed-${username}`) === 'true';
        if (mfaDismissed) {
          return;
        }
        const hasMFA = await checkMFAStatus();
        if (!hasMFA) {
          setShowSetup(true);
        }
      } catch (err) {
        console.error('Error in MFASetup mount:', err);
      }
    };
    run();
  }, [checkMFAStatus]);

  const handleSetupTOTP = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const s = await Auth.setupTOTP(user);
      const issuer = isRenault ? 'RENAULT' : isMotrio ? 'MOTRIO' : 'MAIA';
      const url = `otpauth://totp/${issuer}:${user.username}?secret=${s}&issuer=${issuer}`;
      setSecret(s);
      setQrCodeURL(url);
      setError(null);
    } catch (err) {
      setError(err.message || t('maia.mfa.setupFailed', 'Failed to set up TOTP'));
    }
  };

  const handleVerifyTOTP = async () => {
    if (!totpCode || totpCode.length !== 6) {
      setError(t('maia.mfa.invalidCode', 'Please enter a valid 6-digit code'));
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.verifyTotpToken(user, totpCode);
      await Auth.setPreferredMFA(user, 'TOTP');
      const u = await Auth.currentAuthenticatedUser();
      if (u && u.username) {
        localStorage.removeItem(`mfa-setup-dismissed-${u.username}`);
      }
      setShowSetup(false);
      setQrCodeURL(null);
      setSecret(null);
      setTotpCode('');
      setVerifying(false);
      setError(null);
      alert(t('maia.mfa.successAlert', 'MFA has been successfully set up!'));
    } catch (err) {
      setError(err.message || t('maia.mfa.verifyFailed', 'Invalid code. Please try again.'));
      setVerifying(false);
    }
  };

  const handleDismiss = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const username = user.username;
      localStorage.setItem(`mfa-setup-dismissed-${username}`, 'true');
    } catch (err) {
      console.error('Error in handleDismiss:', err);
    }
    setShowSetup(false);
    setQrCodeURL(null);
    setSecret(null);
    setTotpCode('');
    setError(null);
  };

  const copyToClipboard = (text) => {
    const done = () => alert(t('maia.mfa.copiedAlert', 'Copied to clipboard!'));
    navigator.clipboard.writeText(text).then(done).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      done();
    });
  };

  if (!showSetup) return null;

  return (
    <>
      <div
        role="presentation"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999,
        }}
        onClick={handleDismiss}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: '#fff',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          maxWidth: '400px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <h3 style={{ marginTop: 0 }}>{t('maia.mfa.title', 'Set Up Two-Factor Authentication')}</h3>

        {!qrCodeURL ? (
          <div>
            <p>{t('maia.mfa.intro', 'Enhance your account security by setting up two-factor authentication.')}</p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <Button type="primary" onClick={handleSetupTOTP}>
                {t('maia.mfa.setUpCta', 'Set Up MFA')}
              </Button>
              <Button onClick={handleDismiss}>{t('maia.mfa.maybeLater', 'Maybe Later')}</Button>
            </div>
          </div>
        ) : (
          <div>
            <p>
              <strong>
                {t(
                  'maia.mfa.scanQr',
                  'Scan this QR code with your authenticator app (1password, Google Authenticator, Authy, etc.):'
                )}
              </strong>
            </p>
            <div
              style={{
                textAlign: 'center',
                margin: '15px 0',
                padding: '15px',
                background: '#f5f5f5',
                borderRadius: '4px',
              }}
            >
              {qrCodeURL && (
                <div
                  style={{
                    marginBottom: '15px',
                    display: 'inline-block',
                    padding: '10px',
                    background: '#fff',
                    borderRadius: '4px',
                  }}
                >
                  <QRCodeSVG value={qrCodeURL} size={200} level="M" includeMargin />
                </div>
              )}
              <details style={{ marginTop: '10px', fontSize: '12px' }}>
                <summary style={{ cursor: 'pointer', color: '#666', marginBottom: '5px' }}>
                  {t('maia.mfa.qrSummary', 'Show QR Code URL (for advanced users)')}
                </summary>
                <div
                  style={{
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    fontSize: '11px',
                    marginTop: '8px',
                    padding: '8px',
                    background: '#fff',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                  }}
                >
                  {qrCodeURL}
                </div>
              </details>
              {qrCodeURL && (
                <Button size="small" onClick={() => copyToClipboard(qrCodeURL)} style={{ marginBottom: '10px' }}>
                  {t('maia.mfa.copyQrUrl', 'Copy QR Code URL')}
                </Button>
              )}
              {secret && (
                <div style={{ marginTop: '10px' }}>
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                    {t('maia.mfa.manualSecret', 'Or manually enter this secret key:')}
                  </p>
                  <div
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      padding: '8px',
                      background: '#fff',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      wordBreak: 'break-all',
                    }}
                  >
                    {secret}
                  </div>
                  <Button size="small" onClick={() => copyToClipboard(secret)} style={{ marginTop: '5px' }}>
                    {t('maia.mfa.copySecret', 'Copy Secret Key')}
                  </Button>
                </div>
              )}
            </div>

            <div style={{ marginTop: '15px' }}>
              <label htmlFor="maia-mfa-totp">
                <strong>{t('maia.mfa.enterCode', 'Enter the 6-digit code from your app:')}</strong>
                <input
                  id="maia-mfa-totp"
                  type="text"
                  value={totpCode}
                  onChange={(e) => {
                    setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setError(null);
                  }}
                  placeholder={t('maia.mfa.placeholder', '000000')}
                  maxLength={6}
                  style={{
                    width: '100%',
                    padding: '8px',
                    marginTop: '8px',
                    fontSize: '18px',
                    textAlign: 'center',
                    letterSpacing: '4px',
                    fontFamily: 'monospace',
                  }}
                />
              </label>
            </div>

            {error && (
              <div
                style={{
                  color: '#ff4d4f',
                  marginTop: '10px',
                  fontSize: '14px',
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <Button type="primary" onClick={handleVerifyTOTP} loading={verifying} disabled={totpCode.length !== 6}>
                {t('maia.mfa.verifyEnable', 'Verify & Enable')}
              </Button>
              <Button onClick={handleDismiss}>{t('maia.mfa.cancel', 'Cancel')}</Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function NotFoundPage() {
  const t = useMaiaT();
  return t('maia.notFound', 'not found');
}

function Sidebar() {
  const t = useMaiaT();
  const [menuItems, setMenuItems] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [selectableSegments, setSelectableSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const contentMarginLeft = collapsed ? 80 : 200;

  useEffect(() => {
    Auth.currentAuthenticatedUser({ bypassCache: false }).then((u) => {
      const groups = get(u, ['signInUserSession', 'idToken', 'payload', 'cognito:groups'], []);
      const segments = getSelectableSegmentsFromGroups(groups);
      const nextSelectedSegment = getSelectedSegmentFromGroups(groups);

      setSelectableSegments(segments);
      setSelectedSegment(nextSelectedSegment);
      setStoredSelectedSegment(nextSelectedSegment);
      setMenuItems(getMenuItemsForGroups(groups));
      setUser(u);
    });
  }, []);

  const onCollapse = (nextCollapsed) => {
    setCollapsed(nextCollapsed);
  };

  const canInstallApp = (item) => menuItems.length === 0 || [...menuItems].includes(toUpper(snakeCase(item)));
  const conditionallyRenderRoute = (path, component, appName) =>
    canInstallApp(appName) && <Route key={path} path={`/${path}`} component={component} />;

  if (!user) return null;
  const { given_name: givenName } = user.attributes;
  const defaultRoute = canInstallApp(DASHBOARD) ? '/dashboard' : '/garages';
  const partnerLabel = collapsed ? 'M' : 'MOTRIO';

  return (
    <Layout style={{ minHeight: window.innerHeight, flexDirection: 'row' }}>
      <SiderStyled
        collapsible
        collapsed={collapsed}
        onCollapse={onCollapse}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
        }}
      >
        {isRenault ? (
          <div style={{ display: 'flex' }}>
            <img
              style={{ margin: '10px auto', height: 25 }}
              src={collapsed ? '/assets/images/renault-logo.svg' : '/assets/images/renault-logo--lockup.svg'}
              alt="Renault"
            />
          </div>
        ) : isMotrio ? (
          <div style={{ display: 'flex' }}>
            <BrandLabel>{partnerLabel}</BrandLabel>
          </div>
        ) : (
          (!collapsed && (
            <div style={{ display: 'flex' }}>
              <img style={{ margin: '10px auto', height: 25 }} src="/assets/images/logo--lockup.svg" alt="" />
            </div>
          )) || (
            <div style={{ display: 'flex' }}>
              <img style={{ margin: '10px auto', height: 25 }} src="/assets/images/fixtagone1.svg" alt="" />
            </div>
          )
        )}
        <div style={{ display: 'flex' }}>
          <p style={{ color: siderForeground, margin: 'auto' }}>
            {(!collapsed && t('maia.shell.hello', 'Hello {name}', { name: capitalize(givenName) })) ||
              capitalize(givenName)}
          </p>
        </div>
        <div style={{ display: 'flex' }}>
          <Button
            type="link"
            onClick={async () => {
              try {
                const u2 = await Auth.currentAuthenticatedUser();
                if (u2 && u2.username) {
                  localStorage.removeItem(`mfa-setup-dismissed-${u2.username}`);
                }
              } catch (err) {
                // continue sign out
              }
              await Auth.signOut();
              history.push('/');
            }}
            style={{ margin: 'auto' }}
          >
            {t('maia.nav.signOut', 'Sign out')}
          </Button>
        </div>
        {showSegmentLanguageBar && !collapsed && (
          <div style={{ padding: '0 12px', margin: '4px 0 8px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {showCountrySwitcher && selectableSegments.length > 0 && (
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
                <span style={{ color: siderForeground, fontSize: 13 }}>{t('maia.common.country', 'Country')}</span>
                <Select
                  value={selectedSegment}
                  onChange={(segment) => {
                    setSelectedSegment(segment);
                    setStoredSelectedSegment(segment);
                  }}
                  style={{ width: '100%' }}
                >
                  {selectableSegments.map((segment) => (
                    <Select.Option key={segment} value={segment}>
                      {getSegmentCountryLabel(segment, t)}
                    </Select.Option>
                  ))}
                </Select>
              </span>
            )}
            <LanguageSelector block />
          </div>
        )}
        <hr />
        <Menu defaultSelectedKeys={sidebarStateMap[window.location.pathname]} mode="inline" theme="dark">
          {canInstallApp(DASHBOARD) && (
            <Menu.Item key="1">
              <Link to="/dashboard">
                <Icon type="pie-chart" />
                <span>{t('maia.nav.dashboard', 'Dashboard')}</span>
              </Link>
            </Menu.Item>
          )}
          {canInstallApp(ALLOCATIONS) && (
            <Menu.Item key="2">
              <Link to="/allocations">
                <Icon type="pie-chart" theme="filled" />
                <span>{t('maia.nav.allocations', 'Allocations')}</span>
              </Link>
            </Menu.Item>
          )}
          {canInstallApp(BOOKINGS) && (
            <Menu.Item key="3">
              <Link to={{ pathname: '/redirect', search: '?redirectTo=/bookings' }}>
                <Icon type="book" />
                <span>{t('maia.nav.bookings', 'Bookings')}</span>
              </Link>
            </Menu.Item>
          )}
          {canInstallApp(CAPACITY) && (
            <Menu.Item key="4">
              <Link to="/capacity">
                <Icon type="calendar" />
                <span>{t('maia.nav.capacity', 'Capacity')}</span>
              </Link>
            </Menu.Item>
          )}
          {canInstallApp(GARAGES) && (
            <Menu.Item key="5">
              <Link to={{ pathname: '/redirect', search: '?redirectTo=/garages' }}>
                <Icon type="tool" />
                <span>{t('maia.nav.garages', 'Garages')}</span>
              </Link>
            </Menu.Item>
          )}
          {canInstallApp(COVERAGE_ZONES) && (
            <Menu.Item key="6">
              <Link to={{ pathname: '/redirect', search: '?redirectTo=/coverage-zones' }}>
                <Icon type="environment" />
                <span>{t('maia.nav.coverageZones', 'Coverage Zones')}</span>
              </Link>
            </Menu.Item>
          )}
          {canInstallApp(HEROES) && (
            <Menu.Item key="7">
              <Link to={{ pathname: '/redirect', search: '?redirectTo=/heroes' }}>
                <Icon type="user" />
                <span>{t('maia.nav.heroes', 'Heroes')}</span>
              </Link>
            </Menu.Item>
          )}
          {canInstallApp(PRODUCT) && (
            <Menu.Item key="8">
              <Link to={{ pathname: '/redirect', search: '?redirectTo=/product' }}>
                <Icon type="barcode" />
                <span>{t('maia.nav.products', 'Products')}</span>
              </Link>
            </Menu.Item>
          )}
          {canInstallApp(CAMPAIGNS) && (
            <Menu.Item key="10">
              <Link to={{ pathname: '/redirect', search: '?redirectTo=/campaigns' }}>
                <Icon type="line-chart" />
                <span>{t('maia.nav.campaigns', 'Campaigns')}</span>
              </Link>
            </Menu.Item>
          )}
        </Menu>
      </SiderStyled>
      <Layout style={{ marginLeft: contentMarginLeft, ...transitionStyle }}>
        <Content>
          <Switch>
            <Route exact path="/" render={() => <Redirect to={defaultRoute} />} />
            {conditionallyRenderRoute('dashboard', Dashboard, DASHBOARD)}
            {conditionallyRenderRoute('allocations', Allocations, ALLOCATIONS)}
            {conditionallyRenderRoute('bookings', Bookings, BOOKINGS)}
            {conditionallyRenderRoute('capacity', Capacity, CAPACITY)}
            {conditionallyRenderRoute('garages', Garages, GARAGES)}
            {conditionallyRenderRoute('coverage-zones', CoverageZones, COVERAGE_ZONES)}
            {conditionallyRenderRoute('heroes', Heroes, HEROES)}
            {conditionallyRenderRoute('product', Productlist, PRODUCT)}
            {conditionallyRenderRoute('campaigns', Campaigns, CAMPAIGNS)}
            <Route
              path="/redirect"
              render={({ location }) => {
                const redirectTo = new URLSearchParams(location.search);
                const redirectParam = redirectTo.get('redirectTo');
                return <Redirect to={redirectParam || defaultRoute} />;
              }}
            />
            <Route path="*" component={NotFoundPage} />
          </Switch>
        </Content>
      </Layout>
    </Layout>
  );
}

export default function AppShell() {
  return (
    <>
      <MFASetup />
      <Sidebar />
    </>
  );
}
